import React, { useState, useEffect, useMemo } from 'react';
import { 
  CheckCircle2, AlertCircle, Activity, RefreshCw, 
  PieChart as PieChartIcon, Clock, ArrowUpRight, Layers, Bug, Calendar,
  TrendingUp // <-- Thêm icon này vào đây để hết lỗi
} from 'lucide-react';
import { 
  AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, 
  Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";

const Dashboard = () => {
  const [data, setData] = useState({ submissions: [], errors: [] });
  const [loading, setLoading] = useState(false);
  const [tableFilter, setTableFilter] = useState('all');
  const navigate = useNavigate();

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate('/login'); return; }
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API_BASE}/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/xml-errors`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const submissions = results[0].status === 'fulfilled' ? results[0].value.data : [];
      const errors = results[1].status === 'fulfilled' ? results[1].value.data : [];
      setData({ 
        submissions: Array.isArray(submissions) ? submissions : [], 
        errors: Array.isArray(errors) ? errors : [] 
      });
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setTimeout(() => setLoading(false), 400);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const stats = useMemo(() => {
    const subs = data.submissions || [];
    const errs = data.errors || [];

    const total = subs.length;
    const valid = subs.filter(s => s.status?.toLowerCase() === 'hop_le').length;
    const errorCount = total - valid;
    const validRate = total > 0 ? Math.round((valid / total) * 100) : 0;

    const errorMap = {};
    errs.forEach(e => { if (e.error_code) errorMap[e.error_code] = (errorMap[e.error_code] || 0) + 1; });
    const errorPie = Object.keys(errorMap).map(key => ({ name: key, value: errorMap[key] }))
      .sort((a,b) => b.value - a.value).slice(0, 5);

    const qualityPie = [
      { name: 'Hợp lệ', value: valid },
      { name: 'Có lỗi', value: errorCount }
    ];

    const dailyGroups = subs.reduce((acc, curr) => {
      if (!curr.created_at) return acc;
      const day = curr.created_at.split('T')[0];
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});

    const areaData = Object.keys(dailyGroups).sort().map(day => {
      const [y, m, d] = day.split('-');
      return { label: `${d}/${m}`, count: dailyGroups[day] };
    }).slice(-7);

    const filteredList = subs.filter(s => {
      if (tableFilter === 'valid') return s.status?.toLowerCase() === 'hop_le';
      if (tableFilter === 'error') return s.status?.toLowerCase() === 'co_loi';
      return true;
    }).slice(0, 20);

    return { total, valid, error: errorCount, validRate, errorPie, qualityPie, areaData, filteredList };
  }, [data, tableFilter]);

  return (
    <div className="h-full w-full overflow-y-auto bg-[#fafbfc] p-3 lg:p-5 custom-scrollbar pb-24 lg:pb-8">
      
      {/* HEADER */}
      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-2 px-2">
          <div className="p-2 bg-white rounded-lg border border-slate-100 shadow-sm text-sky-500"><Calendar size={18}/></div>
          <div>
            <h1 className="text-sm lg:text-base font-bold text-slate-700 leading-none">Hệ thống Giám sát</h1>
            <p className="text-[10px] text-slate-400 font-medium mt-1 uppercase tracking-widest leading-none">Dữ liệu XML thời gian thực</p>
          </div>
        </div>
        <button onClick={fetchData} className="p-2 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50 transition-all active:scale-90">
          <RefreshCw size={16} className={`${loading ? 'animate-spin text-sky-500' : 'text-slate-400'}`} />
        </button>
      </div>

      {/* STATS GRID */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 px-1">
        <StatCard title="Tiếp nhận" value={stats.total} icon={<Layers size={16}/>} color="sky" />
        <StatCard title="Hợp lệ" value={stats.valid} icon={<CheckCircle2 size={16}/>} color="emerald" />
        <StatCard title="Có lỗi" value={stats.error} icon={<AlertCircle size={16}/>} color="rose" />
        <StatCard title="Tỉ lệ sạch" value={`${stats.validRate}%`} icon={<TrendingUp size={16}/>} color="indigo" />
      </div>

      {/* CHART ROW: LỖI & CHẤT LƯỢNG */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex flex-col items-center min-h-[300px]">
          <h3 className="text-[11px] font-bold text-slate-500 w-full mb-4 flex items-center gap-2 uppercase tracking-widest"><Bug size={14} className="text-rose-500"/> Phân bổ mã lỗi</h3>
          <div className="w-full h-full min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.errorPie} innerRadius={50} outerRadius={70} paddingAngle={4} dataKey="value" stroke="none">
                  {stats.errorPie.map((_, i) => <Cell key={i} fill={['#f43f5e','#fb7185','#fda4af','#fcd34d','#fbbf24'][i%5]} />)}
                </Pie>
                <Tooltip contentStyle={{fontSize: '10px', borderRadius: '8px', border: 'none'}} />
                <Legend iconType="circle" wrapperStyle={{fontSize: '9px', fontWeight: 'bold'}} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex flex-col items-center min-h-[300px] relative">
          <h3 className="text-[11px] font-bold text-slate-500 w-full mb-4 flex items-center gap-2 uppercase tracking-widest"><PieChartIcon size={14} className="text-indigo-500"/> Chất lượng hồ sơ</h3>
          <div className="w-full h-full min-h-[220px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.qualityPie} innerRadius={60} outerRadius={80} paddingAngle={8} dataKey="value" stroke="none">
                  <Cell fill="#10b981" /><Cell fill="#f43f5e" />
                </Pie>
                <Tooltip contentStyle={{fontSize: '10px', borderRadius: '8px'}} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-slate-700">{stats.validRate}%</span>
              <span className="text-[8px] font-bold text-slate-400 uppercase">Hợp lệ</span>
            </div>
          </div>
        </div>
      </div>

      {/* AREA CHART: LƯU LƯỢNG */}
      <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm mb-6 mx-1">
        <h3 className="text-[11px] font-bold text-slate-500 mb-6 flex items-center gap-2 uppercase tracking-widest"><Activity size={14} className="text-sky-500"/> Lưu lượng hồ sơ gửi theo ngày</h3>
        <div className="h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.areaData} margin={{ left: -25, right: 5 }}>
              <defs>
                <linearGradient id="gSky" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.1}/><stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} />
              <Tooltip contentStyle={{borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', fontSize: '11px'}} />
              <Area type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} fillOpacity={1} fill="url(#gSky)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* TABLE SECTION */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden mx-1">
        <div className="p-4 border-b border-slate-50 flex flex-col sm:flex-row justify-between items-center gap-3">
          <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2"><Clock size={14}/> 20 hồ sơ vừa tiếp nhận</h3>
          <div className="flex bg-slate-50 p-1 rounded-lg">
            {['all', 'valid', 'error'].map(f => (
              <button key={f} onClick={() => setTableFilter(f)} className={`px-3 py-1 text-[9px] font-bold rounded-md transition-all ${tableFilter === f ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-400'}`}>
                {f === 'all' ? 'TẤT CẢ' : f === 'valid' ? 'HỢP LỆ' : 'CÓ LỖI'}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 bg-white/95 backdrop-blur-md z-10 border-b border-slate-50">
              <tr className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                <th className="py-3 px-5">Mã LK</th>
                <th className="py-3 px-4">Họ tên</th>
                <th className="py-3 px-4 text-center">Trạng thái</th>
                <th className="py-3 px-5 text-right">Xem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {stats.filteredList.map((sub, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 group transition-colors">
                  <td className="py-3 px-5 font-medium text-slate-500 tracking-tight">{sub.ma_lk}</td>
                  <td className="py-3 px-4 font-bold text-slate-700">{sub.ho_ten}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${sub.status?.toLowerCase() === 'hop_le' ? 'bg-emerald-50 text-emerald-500' : 'bg-rose-50 text-rose-500'}`}>
                      {sub.status?.toLowerCase() === 'hop_le' ? 'Hợp lệ' : 'Lỗi'}
                    </span>
                  </td>
                  <td className="py-3 px-5 text-right">
                    <button onClick={() => navigate('/xml')} className="p-1 text-slate-300 hover:text-sky-500 transition-colors"><ArrowUpRight size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
      `}</style>
    </div>
  );
};

// COMPONENT CARD
const StatCard = ({ title, value, icon, color }) => {
  const themes = {
    sky: "text-sky-500 bg-sky-50",
    emerald: "text-emerald-500 bg-emerald-50",
    rose: "text-rose-500 bg-rose-50",
    indigo: "text-indigo-500 bg-indigo-50"
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex flex-col gap-2 relative overflow-hidden group hover:shadow-md transition-all">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 ${themes[color]}`}>{icon}</div>
      <div>
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">{title}</p>
        <h4 className="text-xl font-bold text-slate-700 leading-none">{value}</h4>
      </div>
      <div className={`absolute top-1/2 -right-4 -translate-y-1/2 opacity-[0.03] group-hover:opacity-[0.1] grayscale group-hover:grayscale-0 transition-all duration-500 pointer-events-none ${themes[color]}`}>
        {React.cloneElement(icon, { size: 70 })}
      </div>
    </div>
  );
};

export default Dashboard;